import { FileJson, FileUp, History, Play, Workflow } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, cx, Empty, ErrorNote, Modal, Spinner, toast } from "../../components/ui";
import { api, ApiError, useApi, type Me } from "../../lib/api";

interface Row {
  id: string;
  type: string;
  name: string;
  application: string;
  current: number;
  runnable: boolean;
  counts: { mapped: number; dropped: number; review: number; error: number };
  updatedAt: string;
}

/** Job Automation home (design §8.1): automations for this tenant; Designers import legacy XML (phase 2). */
export default function JaHome({ me }: { me: Me }) {
  const { data, error, loading, reload } = useApi<{ automations: Row[] }>("/apps/ja/automations");
  const [importing, setImporting] = useState(false);
  const [importingDesign, setImportingDesign] = useState(false);
  const roles = me.apps?.find((a) => a.id === "ja")?.roles ?? [];
  const designer = roles.includes("designer");
  const runner = roles.includes("run");
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Job Automation</h1>
          <p className="text-sm text-muted">Automations for {me.tenant!.name}.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/apps/ja/runs" className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 h-9 text-sm font-medium text-ink-2 hover:bg-surface-2">
            <History className="size-4" aria-hidden /> Runs
          </Link>
          {designer && (
            <Button onClick={() => setImportingDesign(true)} title="A design exported from this or another portal">
              <FileJson className="size-4" aria-hidden /> Import a design
            </Button>
          )}
          {designer && (
            <Button variant="primary" onClick={() => setImporting(true)}>
              <FileUp className="size-4" aria-hidden /> Import legacy XML
            </Button>
          )}
        </div>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data?.automations.length === 0 ? (
        <Empty icon={<Workflow className="size-8" />} title="No automations yet">
          {designer ? "Import one from the legacy Job Automation XML files." : "A Designer imports or builds them."}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Automation</th>
                <th className="px-4 py-2 font-medium">Version</th>
                <th className="px-4 py-2 font-medium">Import check</th>
                <th className="px-4 py-2 font-medium">Updated</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data?.automations.map((a) => (
                <tr key={a.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 font-mono text-xs">{a.type}</td>
                  <td className="px-4 py-3">
                    <Link to={`/apps/ja/automations/${a.id}`} className="font-medium text-accent-text hover:underline">
                      {a.name}
                    </Link>
                    <div className="text-xs text-muted">{a.application}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-2">v{a.current}</td>
                  <td className="px-4 py-3">
                    <span className={cx(a.counts.error ? "text-bad" : a.counts.review ? "text-warn" : "text-good")}>
                      {a.counts.error ? `${a.counts.error} to fix` : a.counts.review ? `${a.counts.review} to review` : "Ready"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">{new Date(a.updatedAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    {runner && a.runnable && (
                      <Link to={`/apps/ja/automations/${a.id}/run`} className="inline-flex items-center gap-1 text-sm font-medium text-accent-text hover:underline">
                        <Play className="size-4" aria-hidden /> Run
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {importing && <ImportDialog onClose={() => setImporting(false)} onDone={() => (setImporting(false), void reload())} />}
      {importingDesign && <ImportDesignDialog onClose={() => setImportingDesign(false)} onDone={() => (setImportingDesign(false), void reload())} />}
    </div>
  );
}

const FILES = [
  { key: "userfield", label: "User field (prompts)", hint: "…userfield.xml", required: true },
  { key: "parameter", label: "Parameter (field values)", hint: "…parameter.xml", required: true },
  { key: "run", label: "Run (steps)", hint: "…run.xml", required: true },
  { key: "function", label: "Functions", hint: "…function.xml", required: false },
] as const;

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Record<string, File | undefined>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The re-import guard: the automation has Designer edits, so the server asks first (409).
  const [guard, setGuard] = useState<string | null>(null);
  // Second stage (docs/phase4-plan.md decision 3): for each action with reports, must they finish before the next step?
  const [reportSteps, setReportSteps] = useState<{ id: string; name: string; reports: string[] }[] | null>(null);
  const [mustFinish, setMustFinish] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const ready = type.trim() && FILES.every((f) => !f.required || picked[f.key]);

  const read = async () => {
    const files: Record<string, { name: string; text: string }> = {};
    for (const f of FILES) {
      const file = picked[f.key];
      if (file) files[f.key] = { name: file.name, text: await file.text() };
    }
    return files;
  };
  const analyze = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ reportSteps: { id: string; name: string; reports: string[] }[] }>("/apps/ja/import", { body: { type, name, files: await read(), analyze: true } });
      setReportSteps(r.reportSteps);
      setMustFinish(new Set());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const run = async (confirm = false) => {
    setBusy(true);
    setError(null);
    try {
      const files = await read();
      const r = await api<{ head: { id: string; type: string; current: number }; runnable: boolean }>("/apps/ja/import", { body: { type, name, files, source: "upload", reportsMustFinish: [...mustFinish], ...(confirm ? { confirm: true } : {}) } });
      toast(`${r.head.type} imported as version ${r.head.current}${r.runnable ? "" : " (needs attention)"}`);
      onDone();
      navigate(`/apps/ja/automations/${r.head.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setGuard(e.message);
      else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title="Import legacy Job Automation XML"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {reportSteps && !guard && <Button onClick={() => setReportSteps(null)}>Back</Button>}
          {guard ? (
            <Button variant="danger" busy={busy} onClick={() => void run(true)}>
              Import anyway
            </Button>
          ) : reportSteps ? (
            <Button variant="primary" busy={busy} onClick={() => void run()}>
              Import
            </Button>
          ) : (
            <Button variant="primary" busy={busy} disabled={!ready} onClick={() => void analyze()}>
              Next
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error && <ErrorNote>{error}</ErrorNote>}
        {guard && <p className="rounded-lg border border-warn bg-warn-soft p-3 text-ink-2">{guard}</p>}
        {reportSteps ? (
          <div className="space-y-3">
            <p className="text-ink-2">
              Reports run alongside the payroll and never hold it up. Tick an action only if its reports <strong>must finish before the next step starts</strong>; the next step then waits for them.
            </p>
            {reportSteps.length === 0 && <p className="text-muted">This automation makes no reports.</p>}
            <ul className="divide-y divide-line rounded-lg border border-line">
              {reportSteps.map((r) => (
                <li key={r.id} className="px-3 py-2">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={mustFinish.has(r.id)}
                      onChange={(e) =>
                        setMustFinish((m) => {
                          const n = new Set(m);
                          if (e.target.checked) n.add(r.id);
                          else n.delete(r.id);
                          return n;
                        })
                      }
                    />
                    <span>
                      <span className="font-medium text-ink">{r.name}</span> <span className="font-mono text-xs text-muted">{r.id}</span>
                      <span className="block text-xs text-muted">Reports: {r.reports.join(", ")}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted">You can change this later in the Designer, on the next step's Waits for.</p>
          </div>
        ) : (
          <>
        <p className="text-muted">Choose one automation's files, as listed for its type in automationdefinition.xml. Nothing is written back to the legacy storage.</p>
        <div className="grid grid-cols-3 gap-3">
          <label>
            <span className="text-ink-2 font-medium">Type</span>
            <input className={input} value={type} onChange={(e) => setType(e.target.value.toUpperCase())} placeholder="NV11" />
          </label>
          <label className="col-span-2">
            <span className="text-ink-2 font-medium">Name</span>
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="V11 Demo Payroll Automation" />
          </label>
        </div>
        {FILES.map((f) => (
          <label key={f.key} className="block">
            <span className="text-ink-2 font-medium">
              {f.label}
              {!f.required && <span className="text-muted font-normal"> (optional)</span>}
            </span>
            <input type="file" accept=".xml,text/xml" className="block w-full text-sm mt-1" onChange={(e) => setPicked((p) => ({ ...p, [f.key]: e.target.files?.[0] }))} />
            <span className="text-xs text-muted">{picked[f.key]?.name ?? f.hint}</span>
          </label>
        ))}
          </>
        )}
      </div>
    </Modal>
  );
}

interface Bundle {
  format: string;
  type: string;
  name: string;
  current: number;
  exportedAt: string;
  exportedBy: string;
  from?: { tenant?: string };
  versions: { version: number; note?: string }[];
}

/**
 * Imports a design exported from a portal (Designer → Export): a new type comes in with its version history; an
 * existing type gets the export's current version as its next version, after confirming (docs/phase3-plan.md §4,
 * how an automation moves from the sandbox portal to production).
 */
function ImportDesignDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [bundle, setBundle] = useState<Bundle | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guard, setGuard] = useState<string | null>(null);
  const navigate = useNavigate();

  const pick = async (file: File | undefined) => {
    setError(null);
    setGuard(null);
    setBundle(null);
    if (!file) return;
    setFileName(file.name);
    try {
      const b = JSON.parse(await file.text()) as Bundle;
      if (b?.format !== "iap.ja.export/1" || !Array.isArray(b.versions)) throw new Error("That file isn't a Job Automation export");
      setBundle(b);
    } catch (e) {
      setError(e instanceof SyntaxError ? "That file isn't JSON" : (e as Error).message);
    }
  };
  const run = async (confirm = false) => {
    if (!bundle) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ head: { id: string; type: string; current: number } }>("/apps/ja/import-design", { body: { bundle, ...(confirm ? { confirm: true } : {}) } });
      toast(`${r.head.type} imported: now at version ${r.head.current}`);
      onDone();
      navigate(`/apps/ja/automations/${r.head.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setGuard(e.message);
      else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Import a design"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {guard ? (
            <Button variant="danger" busy={busy} onClick={() => void run(true)}>
              Add as the next version
            </Button>
          ) : (
            <Button variant="primary" busy={busy} disabled={!bundle} onClick={() => void run()}>
              Import
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {error && <ErrorNote>{error}</ErrorNote>}
        {guard && <p className="rounded-lg border border-warn bg-warn-soft p-3 text-ink-2">{guard}</p>}
        <p className="text-muted">Choose a file exported from the Designer (Export). This is how an automation moves from the sandbox portal to production. Nothing is sent to Infor.</p>
        <label className="block">
          <span className="text-ink-2 font-medium">Export file</span>
          <input type="file" accept=".json,application/json" className="block w-full text-sm mt-1" onChange={(e) => void pick(e.target.files?.[0])} />
          <span className="text-xs text-muted">{fileName || "…-export.json"}</span>
        </label>
        {bundle && (
          <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1 rounded-lg border border-line p-3">
            <dt className="text-muted">Automation</dt>
            <dd>
              {bundle.name} <span className="font-mono text-xs">({bundle.type})</span>
            </dd>
            <dt className="text-muted">Versions</dt>
            <dd>
              {bundle.versions.length}, current v{bundle.current}
            </dd>
            <dt className="text-muted">Exported</dt>
            <dd>
              {new Date(bundle.exportedAt).toLocaleString()} by {bundle.exportedBy}
              {bundle.from?.tenant ? ` from ${bundle.from.tenant}` : ""}
            </dd>
          </dl>
        )}
      </div>
    </Modal>
  );
}
